/**
 * MonthlyMarginChart — admin-Overview chart showing total profit per
 * month, broken down by customer. Drives the "which customers are
 * actually worth keeping" decision at a glance.
 *
 * Data source: GET /api/work-orders/margin/monthly?months=N — already
 * cleaned + per-month bucketed on the backend. Same revenue / cost
 * formula as `BillingPanel.MarginBadge` so the per-job and aggregated
 * views never disagree.
 *
 * Visual:
 *   - Stacked bar chart: months on X, profit £ on Y, top 5 customers
 *     coloured + everyone else collapsed into a slate "Other" bar.
 *   - Tooltip shows £ per customer per month with margin % beside the
 *     name (computed against that customer's revenue this month).
 *   - Below: top-5 customers table — total profit / revenue / WO count
 *     across the window with margin %, so admins can sort the chart
 *     visually with the table.
 *
 * The "5 customers" cap is a deliberate visual budget. Adding a 6th
 * colour in a stacked bar makes everything muddier; "Other" rolls up
 * the long-tail and is still clickable in the table.
 */
import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, Cell,
} from 'recharts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { TrendingUp, Palette } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { getMonthlyMargin, updateUser } from '../services/api';

// 5 colourblind-safe accent colours + slate for "Other". Picked to read
// distinctly against a white card background while staying calm.
const CUSTOMER_COLOURS = ['#0f766e', '#7c3aed', '#0369a1', '#b45309', '#be185d'];
const OTHER_COLOUR = '#94a3b8';
// Swatches offered in the customer colour-picker popover. Kept small
// so admins pick from a curated palette — but they can also type any
// hex code into the free-text input if these don't fit.
const SWATCH_PALETTE = [
  '#0f766e', '#0369a1', '#7c3aed', '#be185d', '#b45309',
  '#059669', '#2563eb', '#9333ea', '#dc2626', '#ea580c',
  '#0891b2', '#4338ca', '#c026d3', '#e11d48', '#65a30d',
  '#0d9488', '#1d4ed8', '#7c2d12', '#831843', '#365314',
];
// Colour used when the chart is filtered to a single customer — a
// deep emerald that matches the "positive margin" tone used in the
// customer-summary pills below.
const BAR_SOLO_COLOUR = '#0f766e';
const TOP_N = 5;

const fmtMoney = (v) => `£${(Number(v) || 0).toLocaleString('en-GB', {
  minimumFractionDigits: 0, maximumFractionDigits: 0,
})}`;
const fmtMoneyDecimal = (v) => `£${(Number(v) || 0).toLocaleString('en-GB', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;
const fmtMonth = (m) => {
  if (!m) return '';
  const [, mm] = m.split('-');
  const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return monthNames[parseInt(mm, 10) - 1] || m;
};

const fmtPct = (rev, profit) => {
  if (!rev || rev <= 0) return '—';
  return `${((profit / rev) * 100).toFixed(0)}%`;
};

/** Picks the top-N customers by total profit across the window and
 *  builds a colour-keyed lookup. Everyone else collapses into "Other". */
const buildCustomerSlots = (customers) => {
  const sorted = [...customers].sort((a, b) => b.profit - a.profit);
  const top = sorted.slice(0, TOP_N);
  const slots = new Map(top.map((c, i) => [c.customerId, {
    key: c.customerId,
    label: c.customerName,
    color: c.chartColor || CUSTOMER_COLOURS[i],
  }]));
  if (sorted.length > TOP_N) {
    slots.set('__other__', {
      key: '__other__',
      label: 'Other',
      color: OTHER_COLOUR,
    });
  }
  return slots;
};

/** Reshape the API months[] into a recharts-friendly array where each
 *  row has the month + a £-profit field per customer slot. */
const buildChartRows = (months, slots) => months.map((m) => {
  const row = {
    month: m.month,
    monthLabel: fmtMonth(m.month),
    totalProfit: m.profit,
    totalRevenue: m.revenue,
    breakdown: [],
  };
  let otherProfit = 0;
  let otherRevenue = 0;
  m.byCustomer.forEach((c) => {
    if (slots.has(c.customerId)) {
      row[c.customerId] = c.profit;
      row.breakdown.push({ ...c, color: slots.get(c.customerId).color });
    } else {
      otherProfit += c.profit;
      otherRevenue += c.revenue;
    }
  });
  if (slots.has('__other__')) {
    row.__other__ = otherProfit;
    if (otherProfit !== 0 || otherRevenue !== 0) {
      row.breakdown.push({
        customerId: '__other__',
        customerName: 'Other',
        profit: otherProfit,
        revenue: otherRevenue,
        color: OTHER_COLOUR,
      });
    }
  }
  return row;
});

const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0].payload;
  const items = [...(row.breakdown || [])].sort((a, b) => b.profit - a.profit);
  return (
    <div
      className="rounded-md border border-slate-200 bg-white shadow-lg p-3 text-xs min-w-[220px]"
      data-testid="margin-chart-tooltip"
    >
      <div className="font-semibold text-slate-900 mb-2">
        {fmtMonth(row.month)} {row.month?.slice(0, 4)}
      </div>
      {items.length === 0 && (
        <div className="text-slate-400 italic">No completed work orders.</div>
      )}
      {items.map((it) => (
        <div
          key={it.customerId}
          className="flex items-center gap-2 py-0.5"
        >
          <span
            className="inline-block h-2.5 w-2.5 rounded-sm shrink-0"
            style={{ background: it.color }}
          />
          <span className="flex-1 truncate text-slate-700">{it.customerName}</span>
          <span className="tabular-nums font-semibold text-slate-900">
            {fmtMoneyDecimal(it.profit)}
          </span>
          <span className="tabular-nums text-slate-500 w-9 text-right">
            {fmtPct(it.revenue, it.profit)}
          </span>
        </div>
      ))}
      <div className="border-t border-slate-200 mt-2 pt-1.5 flex items-center gap-2 text-slate-900 font-semibold">
        <span className="flex-1">Total profit</span>
        <span className="tabular-nums">{fmtMoneyDecimal(row.totalProfit)}</span>
        <span className="tabular-nums text-slate-500 w-9 text-right">
          {fmtPct(row.totalRevenue, row.totalProfit)}
        </span>
      </div>
    </div>
  );
};

export const MonthlyMarginChart = () => {
  const [months, setMonths] = useState(6);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Selected customer filter. `__all__` (default) shows the stacked
  // top-N bars just like before. Selecting a specific customer ID
  // narrows the chart down to that customer's monthly profit so you
  // can zoom in on one account. Users on the smaller side of the
  // book asked for this because when one customer dwarfs the rest,
  // the smaller customers looked invisible.
  const [customerFilter, setCustomerFilter] = useState('__all__');
  // Chart bar layout — 'stacked' (default) piles customers into one
  // bar per month, 'grouped' puts them side-by-side. Grouped is
  // easier for eyeballing which customer is bigger this month.
  const [viewMode, setViewMode] = useState('stacked');
  // Bump to trigger a data refetch after a customer's chartColor is
  // saved via the popover — without this, the coloured swatches on
  // the top table and the chart bars won't repaint until you change
  // the month range or reload the page.
  const [reloadKey, setReloadKey] = useState(0);
  const reload = () => setReloadKey((n) => n + 1);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    getMonthlyMargin(months)
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || 'Failed to load margin data'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [months, reloadKey]);

  const customerSlots = useMemo(
    () => buildCustomerSlots(data?.customers || []),
    [data],
  );
  const chartRows = useMemo(
    () => buildChartRows(data?.months || [], customerSlots),
    [data, customerSlots],
  );
  const totalProfit = useMemo(
    () => (data?.customers || []).reduce((s, c) => s + c.profit, 0),
    [data],
  );
  const totalRevenue = useMemo(
    () => (data?.customers || []).reduce((s, c) => s + c.revenue, 0),
    [data],
  );
  const totalWoCount = useMemo(
    () => (data?.customers || []).reduce((s, c) => s + (c.woCount || 0), 0),
    [data],
  );

  // Complete alphabetic customer list for the selector dropdown. We
  // don't rely on the top-N slots here — the admin might want to
  // drill into a smaller customer that isn't in the top 5.
  const customerOptions = useMemo(() => {
    const list = [...(data?.customers || [])];
    list.sort((a, b) => (a.customerName || '').localeCompare(b.customerName || ''));
    return list;
  }, [data]);

  // Selected customer's stats (used by the header when a single
  // customer is picked so the top-line figures match the chart).
  const selectedCustomer = useMemo(() => {
    if (customerFilter === '__all__') return null;
    return (data?.customers || []).find((c) => c.customerId === customerFilter) || null;
  }, [customerFilter, data]);

  // When a single customer is picked, project the monthly rows down
  // to just that customer's profit for the same-shape recharts
  // input. Any month where the customer had no completed WOs shows
  // a 0-bar so the X-axis stays continuous.
  const filteredChartRows = useMemo(() => {
    if (customerFilter === '__all__') return chartRows;
    return (data?.months || []).map((m) => {
      const c = (m.byCustomer || []).find((x) => x.customerId === customerFilter);
      return {
        month: m.month,
        monthLabel: fmtMonth(m.month),
        totalProfit: c ? c.profit : 0,
        totalRevenue: c ? c.revenue : 0,
        breakdown: c
          ? [{ ...c, color: BAR_SOLO_COLOUR }]
          : [],
        // Single virtual "solo" series so the BarChart still has a
        // dataKey to hang bars on.
        __solo__: c ? c.profit : 0,
      };
    });
  }, [customerFilter, chartRows, data]);

  // Render-time guard: nothing seeded yet → friendly empty state instead
  // of a 0-axis chart that looks like a render bug.
  const hasAnyData = chartRows.some((r) => Math.abs(r.totalProfit) > 0);

  return (
    <div className="bg-white border border-slate-200 rounded-md" data-testid="monthly-margin-chart">
      <div className="flex items-center justify-between p-5 border-b border-slate-200 gap-3 flex-wrap">
        <div className="min-w-0">
          <h2 className="font-display uppercase text-slate-900 tracking-wide flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-emerald-700" />
            Monthly margin by customer
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Revenue per customer per month across every invoice raised in the last {months} months. Fixed-price completed WOs subtract logged labour + parts cost; hourly / time-clock invoices book the full amount as margin (labour cancels — the sell rate IS the bill).
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-right hidden sm:block">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              {selectedCustomer ? `${selectedCustomer.customerName} profit` : 'Total profit (all customers)'}
            </div>
            <div className="text-lg font-display tabular-nums text-emerald-700" data-testid="margin-chart-total-profit">
              {fmtMoneyDecimal(selectedCustomer ? selectedCustomer.profit : totalProfit)}
            </div>
            <div className="text-[10px] tabular-nums text-slate-500">
              {selectedCustomer
                ? <>{fmtPct(selectedCustomer.revenue, selectedCustomer.profit)} on {fmtMoney(selectedCustomer.revenue)} · {selectedCustomer.woCount} job{selectedCustomer.woCount === 1 ? '' : 's'}</>
                : <>{fmtPct(totalRevenue, totalProfit)} on {fmtMoney(totalRevenue)} · {totalWoCount} job{totalWoCount === 1 ? '' : 's'}</>}
            </div>
          </div>
          {/* Customer selector — "All customers" (stacked top-N) or
              any individual customer (solo emerald bars). Placed
              before the months selector so admins reach it first. */}
          <Select value={customerFilter} onValueChange={setCustomerFilter}>
            <SelectTrigger className="w-44 h-9 bg-white" data-testid="margin-chart-customer-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="__all__">All customers (total)</SelectItem>
              {customerOptions.length > 0 && <div className="h-px bg-slate-200 my-1" />}
              {customerOptions.map((c) => (
                <SelectItem key={c.customerId} value={c.customerId}>
                  {c.customerName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={String(months)} onValueChange={(v) => setMonths(Number(v))}>
            <SelectTrigger className="w-32 h-9 bg-white" data-testid="margin-chart-window-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="3">3 months</SelectItem>
              <SelectItem value="6">6 months</SelectItem>
              <SelectItem value="12">12 months</SelectItem>
            </SelectContent>
          </Select>
          {/* Bar-layout toggle — segmented control. Hidden when a
              single customer is picked because "grouped" needs
              multiple series to be meaningful. */}
          {customerFilter === '__all__' && (
            <div
              className="inline-flex items-center rounded-md border border-slate-200 bg-white p-0.5 h-9"
              data-testid="margin-chart-view-mode"
            >
              <button
                type="button"
                onClick={() => setViewMode('stacked')}
                className={`px-2.5 text-xs font-medium h-8 rounded transition-colors ${
                  viewMode === 'stacked'
                    ? 'bg-slate-900 text-white'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                data-testid="margin-chart-view-stacked"
                aria-pressed={viewMode === 'stacked'}
              >
                Stacked
              </button>
              <button
                type="button"
                onClick={() => setViewMode('grouped')}
                className={`px-2.5 text-xs font-medium h-8 rounded transition-colors ${
                  viewMode === 'grouped'
                    ? 'bg-slate-900 text-white'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                data-testid="margin-chart-view-grouped"
                aria-pressed={viewMode === 'grouped'}
              >
                Grouped
              </button>
            </div>
          )}
        </div>
      </div>

      {loading && (
        <div className="p-12 text-center text-sm text-slate-500" data-testid="margin-chart-loading">
          Loading margin breakdown…
        </div>
      )}
      {!loading && error && (
        <div className="p-8 text-center text-sm text-rose-700" data-testid="margin-chart-error">{error}</div>
      )}
      {!loading && !error && !hasAnyData && (
        <div className="p-12 text-center text-sm text-slate-500" data-testid="margin-chart-empty">
          No completed work orders in the last {months} months yet. Once jobs close their margin will appear here.
        </div>
      )}
      {!loading && !error && hasAnyData && (
        <>
          <div className="px-2 sm:px-5 pt-4 pb-2 h-72" data-testid="margin-chart-bars">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={filteredChartRows} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
                <XAxis
                  dataKey="monthLabel"
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  axisLine={{ stroke: '#e2e8f0' }}
                  tickLine={false}
                />
                <YAxis
                  tickFormatter={(v) => `£${(v / 1000).toFixed(v >= 1000 ? 0 : 1)}k`}
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  axisLine={{ stroke: '#e2e8f0' }}
                  tickLine={false}
                  width={48}
                />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: '#f1f5f9' }} />
                {customerFilter === '__all__' ? (
                  // Stacked or grouped bars — one series per top-N
                  // customer + "Other". `stackId` shared across all
                  // series produces the stack; setting each series to
                  // a unique stackId lays them side-by-side.
                  [...customerSlots.values()].map((slot, idx, arr) => (
                    <Bar
                      key={slot.key}
                      dataKey={slot.key}
                      stackId={viewMode === 'stacked' ? 'profit' : `s-${idx}`}
                      fill={slot.color}
                      isAnimationActive={false}
                      radius={
                        viewMode === 'stacked'
                          ? (slot.key === arr.slice(-1)[0]?.key ? [4, 4, 0, 0] : 0)
                          : [3, 3, 0, 0]
                      }
                    />
                  ))
                ) : (
                  // Single-customer mode — one solo bar per month.
                  <Bar
                    dataKey="__solo__"
                    fill={BAR_SOLO_COLOUR}
                    isAnimationActive={false}
                    radius={[4, 4, 0, 0]}
                  />
                )}
                {customerFilter === '__all__' && (
                  <Legend
                    formatter={(value) => {
                      const slot = customerSlots.get(value);
                      return slot ? slot.label : value;
                    }}
                    iconType="square"
                    wrapperStyle={{ paddingTop: 8, fontSize: 11, color: '#334155' }}
                  />
                )}
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Top customers table */}
          <div className="border-t border-slate-200">
            <div className="px-5 py-2 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center justify-between">
              <span>Top customers · last {months} months</span>
              <Link
                to="/admin/invoices"
                className="text-[11px] text-blue-700 hover:text-blue-800 normal-case tracking-normal"
              >
                View invoices &rarr;
              </Link>
            </div>
            <div className="divide-y divide-slate-100">
              {(data?.customers || []).slice(0, TOP_N).map((c, i) => {
                const margin = c.revenue > 0 ? (c.profit / c.revenue) * 100 : 0;
                let tone = 'bg-emerald-50 text-emerald-800 border-emerald-200';
                if (margin < 10) tone = 'bg-rose-50 text-rose-800 border-rose-200';
                else if (margin < 30) tone = 'bg-amber-50 text-amber-800 border-amber-200';
                const currentColour = c.chartColor || CUSTOMER_COLOURS[i] || OTHER_COLOUR;
                return (
                  <div
                    key={c.customerId}
                    className="flex items-center gap-3 px-5 py-2.5 hover:bg-slate-50"
                    data-testid={`margin-chart-customer-${c.customerId}`}
                  >
                    <CustomerColourSwatch
                      customerId={c.customerId}
                      customerName={c.customerName}
                      currentColour={currentColour}
                      isCustom={!!c.chartColor}
                      onSaved={reload}
                    />
                    <Link
                      to={c.customerId === '__internal__' ? '#' : `/admin/customers/${c.customerId}`}
                      className="flex-1 min-w-0 flex items-center gap-3"
                    >
                      <span className="flex-1 truncate text-sm font-medium text-slate-900">
                        {c.customerName}
                      </span>
                      <span className="hidden md:block text-xs text-slate-500 tabular-nums w-12 text-right">
                        {c.woCount} job{c.woCount === 1 ? '' : 's'}
                      </span>
                      <span className="hidden sm:block text-xs text-slate-500 tabular-nums w-20 text-right">
                        {fmtMoneyDecimal(c.revenue)}
                      </span>
                      <span className="text-sm font-semibold tabular-nums w-24 text-right text-slate-900">
                        {fmtMoneyDecimal(c.profit)}
                      </span>
                      <span
                        className={`inline-flex items-center justify-center w-14 px-1.5 py-0.5 rounded-full border text-[11px] tabular-nums font-semibold ${tone}`}
                      >
                        {margin.toFixed(0)}%
                      </span>
                    </Link>
                  </div>
                );
              })}
              {(data?.customers || []).length === 0 && (
                <div className="px-5 py-6 text-center text-sm text-slate-500">
                  No customer data yet.
                </div>
              )}
              {/* Grand total row — sums every completed WO in the
                  window regardless of the customer filter above. The
                  admin explicitly asked to see "a total for all
                  customers put together", so we surface it at the
                  bottom of the table where the eye lands after
                  scanning the individual customers. */}
              {(data?.customers || []).length > 0 && (
                <div
                  className="flex items-center gap-3 px-5 py-3 bg-slate-50 border-t-2 border-slate-300"
                  data-testid="margin-chart-grand-total"
                >
                  <span className="inline-block h-3 w-3 rounded-sm shrink-0 bg-slate-700" />
                  <span className="flex-1 truncate text-sm font-semibold uppercase tracking-wide text-slate-900">
                    Grand total — all customers
                  </span>
                  <span className="hidden md:block text-xs text-slate-600 tabular-nums w-12 text-right font-semibold">
                    {totalWoCount} job{totalWoCount === 1 ? '' : 's'}
                  </span>
                  <span className="hidden sm:block text-xs text-slate-600 tabular-nums w-20 text-right font-semibold">
                    {fmtMoneyDecimal(totalRevenue)}
                  </span>
                  <span className="text-sm font-bold tabular-nums w-24 text-right text-slate-900">
                    {fmtMoneyDecimal(totalProfit)}
                  </span>
                  <span
                    className="inline-flex items-center justify-center w-14 px-1.5 py-0.5 rounded-full border text-[11px] tabular-nums font-semibold bg-slate-900 text-white border-slate-900"
                  >
                    {(totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0).toFixed(0)}%
                  </span>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MonthlyMarginChart;

// ---- Colour picker for a customer's chart swatch ----
// Click the swatch on a top-customer row → popover with a curated
// palette + a free-text hex input + a "Reset to default" pill. Saving
// PATCHes the user record (chartColor field) and reloads the chart so
// the new colour propagates to the bars immediately.
const CustomerColourSwatch = ({
  customerId, customerName, currentColour, isCustom, onSaved,
}) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hexInput, setHexInput] = useState('');
  const popRef = useRef(null);
  const anchorRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickAway = (e) => {
      if (
        popRef.current && !popRef.current.contains(e.target)
        && anchorRef.current && !anchorRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickAway);
    return () => document.removeEventListener('mousedown', handleClickAway);
  }, [open]);

  useEffect(() => {
    if (open) setHexInput(isCustom ? currentColour : '');
  }, [open, isCustom, currentColour]);

  // "__internal__" isn't a real user row so we can't PATCH a colour
  // onto it — just skip the picker entirely on that pseudo-row.
  const isPickable = customerId && customerId !== '__internal__' && !customerId.startsWith('__');

  const save = async (colour) => {
    if (!isPickable) return;
    setSaving(true);
    try {
      await updateUser(customerId, { chartColor: colour });
      toast.success(colour ? `${customerName} colour saved` : `${customerName} colour reset`);
      setOpen(false);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save colour');
    } finally {
      setSaving(false);
    }
  };

  const applyHex = () => {
    const val = hexInput.trim();
    if (!val) return save(null);
    // Loose hex validation — matches the backend's simple check.
    if (!/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(val)) {
      toast.error('Enter a hex code like #10b981');
      return;
    }
    save(val);
  };

  if (!isPickable) {
    // Non-clickable swatch for pseudo-rows.
    return (
      <span
        className="inline-block h-3 w-3 rounded-sm shrink-0"
        style={{ background: currentColour }}
      />
    );
  }

  return (
    <div className="relative shrink-0">
      <button
        type="button"
        ref={anchorRef}
        onClick={(e) => { e.preventDefault(); setOpen((o) => !o); }}
        className="group h-5 w-5 rounded-sm shrink-0 flex items-center justify-center transition-transform hover:scale-110"
        style={{ background: currentColour }}
        title={isCustom ? `Custom · ${currentColour}` : 'Click to pick a custom colour'}
        data-testid={`swatch-${customerId}`}
        aria-label={`Pick chart colour for ${customerName}`}
      >
        <Palette className="h-2.5 w-2.5 text-white/0 group-hover:text-white/90 transition-colors" />
      </button>
      {open && (
        <div
          ref={popRef}
          className="absolute z-30 mt-2 left-0 w-64 bg-white border border-slate-200 rounded-md shadow-lg p-3 space-y-3"
          data-testid={`swatch-popover-${customerId}`}
        >
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Colour for {customerName}
          </div>
          <div className="grid grid-cols-5 gap-2">
            {SWATCH_PALETTE.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => save(c)}
                disabled={saving}
                className={`h-7 w-7 rounded-md shrink-0 transition-transform hover:scale-110 ${
                  c.toLowerCase() === (currentColour || '').toLowerCase()
                    ? 'ring-2 ring-slate-900 ring-offset-1'
                    : 'ring-1 ring-slate-200'
                }`}
                style={{ background: c }}
                title={c}
                data-testid={`swatch-choice-${c.slice(1)}`}
              />
            ))}
          </div>
          <div>
            <div className="text-[11px] text-slate-500 mb-1">Or type a hex code</div>
            <div className="flex gap-2">
              <input
                type="text"
                value={hexInput}
                onChange={(e) => setHexInput(e.target.value)}
                placeholder="#10b981"
                className="flex-1 h-8 px-2 text-xs font-mono border border-slate-200 rounded focus:outline-none focus:border-slate-400"
                data-testid={`swatch-hex-input-${customerId}`}
              />
              <button
                type="button"
                onClick={applyHex}
                disabled={saving}
                className="h-8 px-3 text-xs font-medium bg-slate-900 text-white rounded hover:bg-slate-800 disabled:opacity-50"
                data-testid={`swatch-hex-save-${customerId}`}
              >
                {saving ? '…' : 'Set'}
              </button>
            </div>
          </div>
          {isCustom && (
            <button
              type="button"
              onClick={() => save(null)}
              disabled={saving}
              className="w-full text-[11px] text-slate-500 hover:text-slate-800 border-t border-slate-100 pt-2"
              data-testid={`swatch-reset-${customerId}`}
            >
              Reset to default palette
            </button>
          )}
        </div>
      )}
    </div>
  );
};
